import axiosInstance from '../../shared/api/axios-instance';
import { useCallback, useEffect, useRef } from 'react';
import { IFetchParams, IUseFetch, RequestResponse } from './use-fetch.interface';
import { useQueryClient } from '@tanstack/react-query';
import { RequestStatuses } from '../../constants';
import useRequestSnackbar from 'hooks/use-request-snackbar';
import { ResponseModel } from 'common/interfaces';
import Axios from 'axios';
import useOverlay from 'hooks/use-overlay';

export const useFetch = <TDataProp extends Object = any>(props?: IUseFetch) => {
  const {
    isExcludeToken,
    isShowSuccessSnackbar = false,
    isShowErrorSnackbar = true,
    isShowWarningSnackbar = false,
    isCancelRequestOnUnmount = false,
    isShowLoading = true,
    loadingKey,
    successMessage: successMessageProp,
    successCallback,
    errorMessage: errorMessageProp,
    errorCallback: errorCallbackProp,
    warningMessage: warningMessageProp,
    resetQueries: resetQueriesProp,
    snackbarDuration: snackbarDurationProp = 3000,
    ...requestConfigProp
  } = props ?? {};

  const [showLoading, hideLoading] = useOverlay(loadingKey);
  const cancelTokenSourceRef = useRef(Axios.CancelToken.source());

  const { showRequestSnackbar } = useRequestSnackbar();

  const queryClient = useQueryClient();

  const isRequestHasStatus = (payload: unknown): payload is ResponseModel => {
    const isRequestStatusExist =
      !payload ||
      Array.isArray(payload) ||
      typeof payload === 'string' ||
      typeof payload === 'number' ||
      typeof payload === 'boolean' ||
      !Object.hasOwn(payload, 'level');

    return !isRequestStatusExist;
  };

  const fetch = async <TData extends Object = TDataProp>({
    loadingKey: loadingKeyProp,
    requestConfig = {},
    callback,
    successMessage,
    errorMessage,
    warningMessage,
    resetQueries = [],
    errorCallback = errorCallbackProp
  }: IFetchParams<TData> = {}) => {
    let response: RequestResponse<TData> = null;
    isShowLoading && showLoading(loadingKeyProp);

    try {
      const customConfig: IUseFetch = {
        ...requestConfig,
        ...requestConfigProp,
        cancelToken: cancelTokenSourceRef.current.token,
        isExcludeToken
      };
      response = await axiosInstance(customConfig);

      callback?.(response);

      if ((isShowSuccessSnackbar || isShowErrorSnackbar || isShowWarningSnackbar) && response?.data) {
        const isRequestSuccess = isRequestHasStatus(response.data)
          ? response.data.level === RequestStatuses.Success
          : response.status === 200;
        const isRequestWarning = isRequestHasStatus(response.data)
          ? response.data.level === RequestStatuses.Warning
          : false;
        const isRequestError = isRequestHasStatus(response.data)
          ? response.data.level === RequestStatuses.Error
          : response.status !== 200;

        const messagesArray = isRequestHasStatus(response.data)
          ? response.data.messages.map(messageItem => messageItem.message)
          : [];
        const messagesFromResponse = messagesArray.length ? messagesArray.join(' ') : undefined;

        const showSnackbar =
          (isShowSuccessSnackbar && isRequestSuccess) ||
          (isShowWarningSnackbar && isRequestWarning) ||
          (isShowErrorSnackbar && isRequestError);
        if (showSnackbar) {
          showRequestSnackbar({
            successCondition: isRequestSuccess,
            successCallback,
            successMessage: successMessageProp || successMessage,
            errorMessage: errorMessageProp || errorMessage || messagesFromResponse,
            duration: snackbarDurationProp,
            warningCondition: isRequestWarning,
            warningMessage: warningMessage || warningMessageProp || messagesFromResponse
          });
        }
      }
    } catch (err) {
      if (!Axios.isCancel(err)) {
        console.error(err, 'error');
        errorCallback && errorCallback();

        if (isShowErrorSnackbar) {
          showRequestSnackbar({
            successCondition: false,
            errorMessage: errorMessageProp || errorMessage,
            duration: snackbarDurationProp
          });
        }
      }

      throw err;
    } finally {
      isShowLoading && hideLoading(loadingKeyProp);

      (resetQueriesProp || resetQueries).map(resetQueryItem =>
        queryClient.resetQueries({ queryKey: [resetQueryItem] })
      );
    }

    return response as NonNullable<RequestResponse<TData>>;
  };

  const cancelFetching = useCallback(() => {
    if (isCancelRequestOnUnmount) {
      cancelTokenSourceRef.current.cancel();
    }
  }, [isCancelRequestOnUnmount]);

  useEffect(() => {
    return cancelFetching;
  }, [cancelFetching]);

  return {
    fetch,
    cancelFetching
  };
};
